import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useAuthConfig, useLogin, useMfaValidate } from '@/api/auth';
import { usePageTitle } from '@/hooks/usePageTitle';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { Button } from '@/components/ui/Button';
import { apiError } from '@/lib/api';
import { BillerBayLogo } from '@/components/ui/BillerBayLogo';
import { ShieldCheck, Lock, Eye, EyeOff } from 'lucide-react';

function MicrosoftLogo({ size = 20 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 21 21" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
      <rect width="10" height="10" fill="#F25022"/>
      <rect x="11" width="10" height="10" fill="#7FBA00"/>
      <rect y="11" width="10" height="10" fill="#00A4EF"/>
      <rect x="11" y="11" width="10" height="10" fill="#FFB900"/>
    </svg>
  );
}

const SSO_ERRORS: Record<string, string> = {
  unauthorized_domain: 'Your Microsoft account domain is not authorized. Contact your administrator.',
  client_account: 'Client accounts cannot sign in with Microsoft. Use email and password.',
  account_disabled: 'Your account has been disabled. Contact your administrator.',
  access_denied: 'Microsoft sign-in was cancelled or denied.',
  invalid_state: 'The sign-in session expired. Please try again.',
  server_error: 'Something went wrong during Microsoft sign-in. Please try again.',
  sso_not_configured: 'Microsoft sign-in is not configured. Use email and password.',
};

const schema = z.object({
  email: z.string().email('Enter a valid email address, such as name@company.com.'),
  password: z.string().min(1, 'Password is required.'),
});

type FormValues = z.infer<typeof schema>;

function safeRedirect(raw: string | null, fallback: string): string {
  if (!raw) return fallback;
  try {
    const decoded = decodeURIComponent(raw);
    if (decoded.startsWith('/') && !decoded.startsWith('//')) return decoded;
  } catch { /* fall through */ }
  return fallback;
}

const FEATURES = [
  'Upload, review, and share files with your BillerBay team — organized and audited.',
  'Track document requests with deadlines, statuses, and real-time notifications.',
  'HIPAA-aware access controls with full activity logs and role-based permissions.',
];

export function LoginPage() {
  usePageTitle('Sign in');
  const [searchParams] = useSearchParams();
  const from = searchParams.get('from');
  const reason = searchParams.get('reason');
  const navigate = useNavigate();
  const login = useLogin();
  const mfaValidate = useMfaValidate();
  const { setSession } = useAuth();
  const { data: authConfig, isLoading: configLoading } = useAuthConfig();
  const [serverError, setServerError] = useState<string | null>(null);
  const [mfaChallenge, setMfaChallenge] = useState<string | null>(null);
  const [mfaCode, setMfaCode] = useState('');
  // Login flow: a first screen lets the user pick "Client login" (email +
  // password) or "BillerBay Staff login" (Microsoft 365). Clients never see the
  // M365 button. When SSO is disabled there's no staff option, so callers fall
  // straight through to the client email form.
  const [authMode, setAuthMode] = useState<'choose' | 'client' | 'staff'>('choose');
  const [showPassword, setShowPassword] = useState(false);
  const ssoError = searchParams.get('sso_error');

  const ssoEnabled = authConfig?.ms_sso_enabled ?? false;
  const ssoUrl = `${import.meta.env.VITE_API_BASE_URL}/auth/microsoft/login${from ? `?next=${encodeURIComponent(from)}` : ''}`;

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    resolver: async (values) => {
      const parsed = schema.safeParse(values);
      if (parsed.success) return { values: parsed.data, errors: {} };
      const fieldErrors: Record<string, { type: string; message: string }> = {};
      for (const issue of parsed.error.issues) {
        const key = issue.path.join('.');
        if (!fieldErrors[key]) fieldErrors[key] = { type: 'validation', message: issue.message };
      }
      return { values: {}, errors: fieldErrors };
    },
  });

  const onSubmit = async (values: FormValues) => {
    setServerError(null);
    try {
      const resp = await login.mutateAsync({ ...values, remember_me: true });
      if (resp.mfa_setup_required && resp.setup_token) {
        sessionStorage.setItem('mfa_setup_token', resp.setup_token);
        navigate('/mfa-setup-required');
        return;
      }
      if (resp.mfa_required && resp.mfa_challenge_token) {
        setMfaChallenge(resp.mfa_challenge_token);
        return;
      }
      setSession(resp);
      navigate(
        safeRedirect(from, resp.user?.role === 'client_admin' || resp.user?.role === 'client_user' ? '/client' : '/staff'),
        { replace: true },
      );
    } catch (e) {
      setServerError(apiError(e));
    }
  };

  const onMfaSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const resp = await mfaValidate.mutateAsync({ mfa_challenge_token: mfaChallenge!, code: mfaCode });
      setSession(resp);
      navigate(safeRedirect(from, resp.user?.role === 'client_admin' || resp.user?.role === 'client_user' ? '/client' : '/staff'));
    } catch {
      setServerError('Invalid code. Check your authenticator app and try again.');
    }
  };

  return (
    <div className="grid min-h-dvh lg:grid-cols-[1.05fr_1fr]">
      {/* Left panel — brand */}
      <aside className="relative hidden flex-col justify-between overflow-hidden bg-[#12122C] p-10 text-white lg:flex">
        <div
          className="pointer-events-none absolute -right-24 -top-24 size-[420px] rounded-full opacity-20 blur-3xl"
          style={{ background: 'radial-gradient(circle, #3F4CFF 0%, rgba(63,76,255,0) 70%)' }}
          aria-hidden
        />
        <div
          className="pointer-events-none absolute -bottom-32 -left-16 size-[360px] rounded-full opacity-15 blur-3xl"
          style={{ background: 'radial-gradient(circle, #00CBDE 0%, rgba(0,203,222,0) 70%)' }}
          aria-hidden
        />
        <BillerBayLogo variant="light" height={26} />
        <div className="relative max-w-md">
          <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[#00CBDE]">
            BillerBay Exchange
          </p>
          <h1 className="mt-3 text-[32px] font-bold leading-[1.15] tracking-tight">
            Secure document exchange for healthcare practices.
          </h1>
          <ul className="mt-8 space-y-3">
            {FEATURES.map((f) => (
              <li key={f} className="flex items-start gap-3 text-sm text-white/80">
                <ShieldCheck size={16} className="mt-0.5 shrink-0 text-[#00CBDE]" aria-hidden />
                {f}
              </li>
            ))}
          </ul>
        </div>
        <p className="relative text-xs text-white/40">
          Access is restricted to authorized users. Sessions and file actions are logged.
        </p>
      </aside>

      {/* Right panel */}
      <main className="flex items-center justify-center bg-[#F2F2F8] px-5 py-10">
        <div className="w-full max-w-[400px]">
          <div className="mb-8 lg:hidden">
            <BillerBayLogo variant="dark" height={28} />
          </div>

          <h2 className="text-2xl font-[650] tracking-tight text-[#12122C]">Sign in</h2>
          <p className="mt-1.5 text-sm text-[#676687]">
            {ssoEnabled && authMode === 'choose'
              ? 'Choose how you sign in to Exchange.'
              : ssoEnabled && authMode === 'staff'
                ? 'BillerBay staff — use your Microsoft 365 account.'
                : 'Client sign-in — use your email address and password.'}
          </p>

          <div className="mt-7 rounded-xl border border-[#E3E3F1] bg-white p-6 shadow-[0_1px_3px_rgba(15,23,42,0.08)]">

            {/* Loading skeleton while auth config resolves */}
            {configLoading && !authConfig ? (
              <div className="flex flex-col gap-3 py-2 animate-pulse">
                <div className="h-11 rounded-lg bg-[#F2F2F8]" />
                <div className="flex items-center gap-3 my-2">
                  <span className="h-px flex-1 bg-[#E3E3F1]" />
                  <span className="h-3 w-20 rounded bg-[#E3E3F1]" />
                  <span className="h-px flex-1 bg-[#E3E3F1]" />
                </div>
                <div className="h-10 rounded-lg bg-[#F2F2F8]" />
              </div>
            ) : /* MFA challenge */
            mfaChallenge ? (
              <div>
                <div className="flex items-center gap-3 mb-5">
                  <div className="flex size-9 shrink-0 items-center justify-center rounded-full bg-[#EFF0FF]">
                    <ShieldCheck size={16} className="text-[#0410BD]" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-[#12122C]">Two-factor authentication</p>
                    <p className="text-xs text-[#676687]">Enter the 6-digit code from your authenticator app.</p>
                  </div>
                </div>
                <form onSubmit={onMfaSubmit} className="flex flex-col gap-4">
                  <div>
                    <Label htmlFor="mfa-code">Authenticator code</Label>
                    <Input
                      id="mfa-code"
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]{6}"
                      maxLength={6}
                      autoComplete="one-time-code"
                      autoFocus
                      value={mfaCode}
                      onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, ''))}
                      placeholder="000000"
                      className="text-center text-xl tracking-[0.5em] font-mono"
                    />
                  </div>
                  {serverError && (
                    <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-4 py-3">{serverError}</div>
                  )}
                  <Button type="submit" variant="primary" loading={mfaValidate.isPending} className="w-full justify-center">
                    Verify
                  </Button>
                  <button type="button" onClick={() => { setMfaChallenge(null); setServerError(null); }} className="w-full text-center text-sm text-[#676687] hover:text-[#12122C] transition-colors">
                    ← Back to sign in
                  </button>
                </form>
              </div>

            ) : ssoEnabled && authMode === 'choose' ? (
              /* ── Choose: Client vs BillerBay Staff ──────────── */
              <div className="flex flex-col gap-3">
                {reason === 'timeout' && (
                  <div className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-4 py-3">
                    You were signed out due to inactivity.
                  </div>
                )}
                {ssoError && (
                  <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-4 py-3">
                    {SSO_ERRORS[ssoError] ?? `Sign-in error: ${ssoError}`}
                  </div>
                )}

                <button
                  type="button"
                  onClick={() => { setAuthMode('client'); setServerError(null); }}
                  className="flex h-11 w-full items-center justify-center gap-2 rounded-lg bg-[#0410BD] text-sm font-semibold text-white outline-none transition-colors hover:bg-[#0310A0] focus-visible:ring-2 focus-visible:ring-[#3F4CFF]/40"
                >
                  Client login
                </button>
                <button
                  type="button"
                  onClick={() => { setAuthMode('staff'); setServerError(null); }}
                  className="flex h-11 w-full items-center justify-center gap-2.5 rounded-lg border border-[#BABACE] bg-white text-sm font-semibold text-[#12122C] outline-none transition-colors hover:bg-[#F2F2F8] focus-visible:ring-2 focus-visible:ring-[#3F4CFF]/30"
                >
                  <MicrosoftLogo size={16} />
                  BillerBay Staff login
                </button>
              </div>

            ) : ssoEnabled && authMode === 'staff' ? (
              /* ── BillerBay Staff — Microsoft 365 ────────────── */
              <div className="flex flex-col gap-4">
                <button
                  type="button"
                  onClick={() => { setAuthMode('choose'); setServerError(null); }}
                  className="flex items-center gap-1.5 text-xs text-[#676687] hover:text-[#12122C] transition-colors mb-1"
                >
                  ← Back
                </button>
                {ssoError && (
                  <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-4 py-3">
                    {SSO_ERRORS[ssoError] ?? `Sign-in error: ${ssoError}`}
                  </div>
                )}
                {serverError && (
                  <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-4 py-3">{serverError}</div>
                )}
                <a
                  href={ssoUrl}
                  className="flex h-11 w-full items-center justify-center gap-2.5 rounded-lg border border-[#BABACE] bg-white text-sm font-semibold text-[#12122C] outline-none transition-colors hover:bg-[#F2F2F8] focus-visible:ring-2 focus-visible:ring-[#3F4CFF]/30"
                >
                  <MicrosoftLogo />
                  Continue with Microsoft
                </a>
              </div>

            ) : (
              /* ── Email / password form ──────────────────────── */
              <>
                {ssoEnabled && (
                  <button
                    type="button"
                    onClick={() => { setAuthMode('choose'); setServerError(null); }}
                    className="flex items-center gap-1.5 text-xs text-[#676687] hover:text-[#12122C] transition-colors mb-5"
                  >
                    ← Back
                  </button>
                )}

                {reason === 'timeout' && (
                  <div className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 mb-4">
                    You were signed out due to inactivity.
                  </div>
                )}
                {ssoError && (
                  <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-4 py-3 mb-4">
                    {SSO_ERRORS[ssoError] ?? `Sign-in error: ${ssoError}`}
                  </div>
                )}

                <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4" noValidate>
                  <div className="flex flex-col gap-1.5">
                    <Label htmlFor="email">Email address</Label>
                    <Input
                      id="email"
                      type="email"
                      autoComplete="email"
                      autoFocus
                      invalid={!!errors.email}
                      placeholder="name@practice.com"
                      {...register('email')}
                    />
                    {errors.email && <p className="text-xs text-red-600">{errors.email.message}</p>}
                  </div>

                  <div className="flex flex-col gap-1.5">
                    <Label htmlFor="password">Password</Label>
                    <div className="relative flex items-center">
                      <Lock size={14} className="pointer-events-none absolute left-3 text-[#BABACE]" aria-hidden />
                      <input
                        id="password"
                        type={showPassword ? 'text' : 'password'}
                        autoComplete="current-password"
                        placeholder="Enter your password"
                        className={[
                          'w-full rounded-[4px] border bg-white px-3 py-2 pl-9 pr-10 text-sm text-[#12122C] placeholder-[#BABACE]',
                          'focus:outline-none focus:ring-[3px] focus:ring-[#3F4CFF]/16 focus:border-[#3F4CFF]',
                          'transition-shadow transition-colors duration-100',
                          errors.password
                            ? 'border-[#DC2626] focus:ring-[#DC2626]/12 focus:border-[#DC2626]'
                            : 'border-[#BABACE]',
                        ].join(' ')}
                        {...register('password')}
                      />
                      <button
                        type="button"
                        tabIndex={-1}
                        onClick={() => setShowPassword((v) => !v)}
                        className="absolute right-3 text-[#BABACE] hover:text-[#676687] transition-colors"
                        aria-label={showPassword ? 'Hide password' : 'Show password'}
                      >
                        {showPassword ? <EyeOff size={14} /> : <Eye size={14} />}
                      </button>
                    </div>
                    {errors.password && <p className="text-xs text-red-600">{errors.password.message}</p>}
                  </div>

                  <div className="flex items-center justify-between">
                    <Link to="/password-reset" className="text-xs font-medium text-[#0410BD] outline-none hover:underline focus-visible:underline">
                      Forgot password?
                    </Link>
                  </div>

                  {serverError && (
                    <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-4 py-3">{serverError}</div>
                  )}

                  <Button
                    type="submit"
                    variant="primary"
                    loading={isSubmitting || login.isPending || configLoading}
                    className="w-full justify-center h-11 text-sm"
                  >
                    Sign in
                  </Button>
                </form>
              </>
            )}
          </div>

          <div className="mt-5 flex flex-col items-center gap-2">
            <p className="text-center text-xs text-[#9FA0BD]">
              Trouble signing in? Email us at{' '}
              <span className="font-medium text-[#676687] select-all">support@billerbay.com</span>
            </p>
          </div>
        </div>
      </main>
    </div>
  );
}
